import React from 'react';
import Style from '../../../../organization/cross_selling/editCrossSelling.module.css';
import useAppContext from '../../../../../context/useAppContext';
import { useTranslation } from 'react-i18next';

import { Card, CheckboxLabel, Form, Icon, Input, Message, OptionalSection, RadioLabel, TextAreaLabel, Tooltip, TranslateDropdown, FieldErrors } from '@weezevent/nacre';
import { getLanguagesTranslateDropdown, getProductsLanguages } from '../../../../organization/cross_selling/list/translationUtils';
import LoadingCard from '../../../../../components/loadingCard';
import SelectTaxes from '../../../../../components/tax/selectTaxes';
import TaxModel from '../../../../../models/taxModel';

export default function RenderFormProduct({ form, disabled = false, setForm, setFormObject, formErrors, variants, transaction_total_count, defaultLanguage, isLoading }) {
    const [{ organization }] = useAppContext();
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(Boolean(form.description));
    const [descLanguage, setDescLanguage] = React.useState(defaultLanguage);

    const [taxes, loadingTaxes] = TaxModel.useApiModel({});

    const productCanBeVisible = React.useMemo(() => {
        return Boolean(variants?.some(vr => !vr.disabled));
    }, [variants]);

    const productsLanguages = React.useMemo(() => getProductsLanguages(organization), [organization]);
    const [productLanguage, setProductLanguage] = React.useState(defaultLanguage);
    const languagesTranslateDropdown = React.useMemo(() => getLanguagesTranslateDropdown(productsLanguages), [productsLanguages]);
    const descriptionDesc = form.translations.description[descLanguage] || '';

    const handleTextChange = React.useCallback(
        (key, event) => {
            delete formErrors[key];
            let ret = { ...form };
            ret.translations[key][productLanguage] = event.target.value || '';
            if (productLanguage === defaultLanguage) ret[key] = event.target.value;
            setFormObject(
                {
                    [key]: ret[key],
                    translations: ret.translations
                },
                true
            );
        },
        [form, formErrors, defaultLanguage, setFormObject]
    );

    return (
        <>
            <LoadingCard loading={loadingTaxes || isLoading}>
                <Card.Content>
                    <div className={Style['add-cross-selling-name']}>
                        <Form.Label required>{t('ticket.cross-selling.add.name')}</Form.Label>
                        <TranslateDropdown
                            defaultValue={defaultLanguage}
                            languages={languagesTranslateDropdown}
                            onChange={e => setProductLanguage(e)}
                            selected={Object.entries(form.translations.name)
                                .map(([k, v]) => (v ? k : undefined))
                                .filter(e => e)}
                        />
                    </div>
                    <Input
                        value={form.translations.name[productLanguage] || ''}
                        errors={formErrors['name'] ? true : false}
                        disabled={disabled}
                        onChange={event => {
                            handleTextChange('name', event);
                        }}
                    />
                    <div className={Style['add-cross-selling-description']}>
                        <OptionalSection optionalText={t('ticket.common.labelOptional')} label={t('ticket.cross-selling.add.addDescription')} open={open} onToggle={setOpen}>
                            <div className={Style['add-cross-selling-description-containt']}>
                                <Form.Label>{t('ticket.cross-selling.add.description')}</Form.Label>
                                <TranslateDropdown
                                    defaultValue={defaultLanguage}
                                    languages={languagesTranslateDropdown}
                                    onChange={e => setDescLanguage(e)}
                                    selected={Object.entries(form.translations.description)
                                        .map(([k, v]) => (v ? k : undefined))
                                        .filter(e => e)}
                                />
                            </div>
                            <TextAreaLabel
                                value={descriptionDesc}
                                onChange={event => {
                                    handleTextChange('description', event);
                                }}
                                maxlength={450}
                                showLength={true}
                                disabled={disabled}
                                placeholder={t('ticket.cross-selling.add.textAreaPlaceholder')}
                                errors={formErrors['description'] ? true : false}
                            />
                            <FieldErrors errors={[t(formErrors['description'])]} />
                        </OptionalSection>
                    </div>
                    <div className={Style['tooltip-no-left']}>
                        <Form.Label>{t('ticket.cross-selling.add.category')}</Form.Label>
                        <RadioLabel
                            checked={form?.category === 2}
                            disabled={transaction_total_count > 0 || disabled}
                            onClick={() => {
                                setForm('category', 2);
                            }}
                            name="category"
                            value="products_category"
                            inline
                            bordered
                        >
                            {t('ticket.cross-selling.add.radioCategory.product')} {<Icon name="shirt" />}
                        </RadioLabel>
                        <RadioLabel
                            checked={form?.category === 3}
                            disabled={transaction_total_count > 0 || disabled}
                            onClick={() => {
                                setForm('category', 3);
                            }}
                            name="category"
                            value="service_category"
                            inline
                            bordered
                        >
                            {t('ticket.cross-selling.add.radioCategory.service')} {<Icon name="service" />}
                        </RadioLabel>
                        <Tooltip triggerOnHover={true} title={t('ticket.cross-selling.add.radioCategory.tooltip_cashless')}>
                            <RadioLabel
                                checked={form?.category === 1}
                                disabled={transaction_total_count > 0 || disabled}
                                onClick={() => {
                                    setFormObject(
                                        {
                                            for_buyer: false,
                                            tva: 0,
                                            category: 1
                                        },
                                        true
                                    );
                                }}
                                name="category"
                                value="cashless_category"
                                inline
                                bordered
                            >
                                {t('ticket.cross-selling.add.radioCategory.cashless')} {<Icon name="credit-card" />}
                            </RadioLabel>
                        </Tooltip>
                    </div>
                    {form.category === 1 && (
                        <Message type="default" title={t('ticket.cross-selling.cashless-message-title')}>
                            {t('ticket.cross-selling.cashless-message-subtitle')}
                        </Message>
                    )}
                    <div>
                        <Form.Label>{t('ticket.cross-selling.add.type_of_cross-selling')}</Form.Label>
                        <RadioLabel
                            disabled={transaction_total_count > 0 || disabled}
                            checked={!form.for_buyer}
                            onClick={() => {
                                setForm('for_buyer', false);
                            }}
                            name="for_buyer"
                            value="per_ticket"
                            inline
                            bordered
                        >
                            {t('ticket.cross-selling.add.radioForBuyer.for_buyer')}
                        </RadioLabel>
                        <RadioLabel
                            disabled={form.category === 1 || transaction_total_count > 0 || disabled}
                            checked={form.for_buyer}
                            onClick={() => {
                                setForm('for_buyer', true);
                            }}
                            name="for_buyer"
                            value="per_order"
                            inline
                            bordered
                        >
                            {t('ticket.cross-selling.add.radioForBuyer.for_order')}
                        </RadioLabel>
                    </div>
                    <div>
                        <SelectTaxes
                            taxes={taxes}
                            required
                            label={t('ticket.cross-selling.add.taxeLabel')}
                            errors={formErrors['tva'] ? [t(formErrors['tva'])] : []}
                            onChange={tva => {
                                setForm('tva', tva);
                            }}
                            value={form.tva}
                            disabled={form.category === 1 || disabled}
                        />
                    </div>
                    <div className={Style['tooltip-no-left']}>
                        <Form.Label>{t('ticket.cross-selling.add.visibility')}</Form.Label>
                        <Tooltip triggerOnHover={!productCanBeVisible} visible={!productCanBeVisible} title={t('ticket.cross-selling.visibility_tooltip')}>
                            <RadioLabel
                                disabled={!productCanBeVisible || disabled}
                                checked={!form?.disabled}
                                onClick={() => {
                                    setForm('disabled', false);
                                }}
                                name="visible_product"
                                value="visible_online"
                                inline
                                bordered
                            >
                                {t('ticket.cross-selling.add.visibleOnline')} {<Icon name="eye" />}
                            </RadioLabel>
                        </Tooltip>
                        <RadioLabel
                            checked={form?.disabled}
                            disabled={disabled}
                            onClick={() => {
                                setForm('disabled', true);
                            }}
                            name="visible_product"
                            value="non_visible_online"
                            inline
                            bordered
                        >
                            {t('ticket.common.action.hide-online')} {<Icon name="eye-slash" />}
                        </RadioLabel>
                    </div>
                    <OptionalSection label={t('ticket.cross-selling.add.showOption')}>
                        <CheckboxLabel
                            label={t('ticket.cross-selling.add.choose_none_option')}
                            onChange={e => setForm('choose_none_option', e.target.checked)}
                            checked={form?.choose_none_option}
                            disabled={disabled}
                        />
                        <CheckboxLabel
                            label={t('ticket.cross-selling.add.display_quantity')}
                            onChange={e => setForm('display_quantity', e.target.checked)}
                            checked={form?.display_quantity}
                            disabled={disabled}
                        />
                        <CheckboxLabel
                            label={t('ticket.cross-selling.add.display_out_of_stock')}
                            onChange={e => setForm('display_out_of_stock', e.target.checked)}
                            checked={form?.display_out_of_stock}
                            disabled={disabled}
                        />
                    </OptionalSection>
                </Card.Content>
            </LoadingCard>
        </>
    );
}

RenderFormProduct.defaultProps = {
    product: null,
    variant: null,
    transaction_total_count: null,
    isLoading: false
};
